import React, { forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import { format, parse, isValid, getYear, getMonth } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import './DateInput.css';

const DATE_DISPLAY_FORMAT = "dd-MMM-yy";
const DATE_VALUE_FORMAT = "yyyy-MM-dd";

const years = [];
for (let i = 1950; i <= getYear(new Date()) + 10; i++) {
    years.push(i);
}

const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const CalendarIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#666"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ pointerEvents: 'none' }}
    >
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="16" y1="2" x2="16" y2="6"></line>
        <line x1="8" y1="2" x2="8" y2="6"></line>
        <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
);

const DateInput = ({
    value,
    onChange,
    name,
    disabled = false,
    maxDate,
    minDate,
    className = "form-control",
    onBlur,
    placeholder = "dd-MMM-yy",
    ...props
}) => {
    const parseValue = (val) => {
        if (!val) return null;
        try {
            const parsed = parse(val, DATE_VALUE_FORMAT, new Date());
            return isValid(parsed) ? parsed : null;
        } catch {
            return null;
        }
    };

    const handleChange = (date) => {
        const formatted = date && isValid(date) ? format(date, DATE_VALUE_FORMAT) : '';
        onChange({ target: { name, value: formatted } });
    };

    const handleBlur = () => {
        if (onBlur) onBlur({ target: { name } });
    };

    return (
        <DatePicker
            selected={parseValue(value)}
            onChange={handleChange}
            onBlur={handleBlur}
            dateFormat={DATE_DISPLAY_FORMAT}
            maxDate={maxDate ? parseValue(maxDate) : undefined}
            minDate={minDate ? parseValue(minDate) : undefined}
            disabled={disabled}
            customInput={<CustomInput disabled={disabled} className={className} placeholder={placeholder} />}
            renderCustomHeader={({
                date,
                changeYear,
                changeMonth,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
            }) => (
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '4px 8px',
                    gap: '4px'
                }}>
                    <button
                        type="button"
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                        style={{
                            border: 'none',
                            background: 'transparent',
                            color: prevMonthButtonDisabled ? '#ccc' : '#666',
                            cursor: prevMonthButtonDisabled ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            padding: '2px 6px',
                            borderRadius: '3px'
                        }}
                        onMouseOver={(e) => !prevMonthButtonDisabled && (e.target.style.background = '#f0f0f0')}
                        onMouseOut={(e) => e.target.style.background = 'transparent'}
                    >
                        ‹
                    </button>

                    <div style={{ display: 'flex', gap: '4px' }}>
                        <select
                            value={getMonth(date)}
                            onChange={(e) => changeMonth(parseInt(e.target.value))}
                            style={{
                                padding: '2px 4px',
                                borderRadius: '3px',
                                border: '1px solid #e0e0e0',
                                fontSize: '11px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                outline: 'none',
                                background: '#fff',
                                color: '#333'
                            }}
                        >
                            {months.map((m, i) => <option key={m} value={i}>{m}</option>)}
                        </select>

                        <select
                            value={getYear(date)}
                            onChange={(e) => changeYear(parseInt(e.target.value))}
                            style={{
                                padding: '2px 4px',
                                borderRadius: '3px',
                                border: '1px solid #e0e0e0',
                                fontSize: '11px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                outline: 'none',
                                background: '#fff',
                                color: '#333'
                            }}
                        >
                            {years.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>

                    <button
                        type="button"
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                        style={{
                            border: 'none',
                            background: 'transparent',
                            color: nextMonthButtonDisabled ? '#ccc' : '#666',
                            cursor: nextMonthButtonDisabled ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            padding: '2px 6px',
                            borderRadius: '3px'
                        }}
                        onMouseOver={(e) => !nextMonthButtonDisabled && (e.target.style.background = '#f0f0f0')}
                        onMouseOut={(e) => e.target.style.background = 'transparent'}
                    >
                        ›
                    </button>
                </div>
            )}
            popperPlacement="bottom-start"
            {...props}
        />
    );
};

const CustomInput = forwardRef(({ value, onClick, disabled, className, placeholder, onChange }, ref) => {
    return (
        <div
            style={{
                position: 'relative',
                width: '100%'
            }}
        >
            <input
                type="text"
                className={className}
                onClick={onClick}
                onChange={onChange}
                ref={ref}
                value={value}
                disabled={disabled}
                placeholder={placeholder}
                style={{
                    fontSize: '13px',
                    paddingRight: '32px'
                }}
            />
            <div
                style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    pointerEvents: 'none',
                    opacity: disabled ? 0.5 : 1
                }}
            >
                <CalendarIcon />
            </div>
        </div>
    );
});

export const formatDateDisplay = (val) => {
    if (!val) return '';
    try {
        const date = parse(val, "yyyy-MM-dd", new Date());
        return isValid(date) ? format(date, "dd-MMM-yy") : val;
    } catch {
        return val;
    }
};

export default DateInput;
